export class ApiError extends Error {
  constructor(status, message) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

const API_ERROR_CODES = {
  LABEL_REQUIRED: 'All questions must have question text.',
  TITLE_REQUIRED: 'Survey title is required.',
  OPTIONS_REQUIRED: 'One or more questions need at least one answer choice.',
  QUESTIONS_INVALID: 'Questions must be a valid list.',
  NO_DRAFT: 'Save a draft with at least one question before publishing.',
  NO_QUESTIONS: 'Add at least one question before publishing.',
};

const API_STATUS_MESSAGES = {
  400: 'Request could not be processed. Check your input.',
  401: 'Please sign in to continue.',
  403: 'You do not have access to this action.',
  404: 'The requested item could not be found.',
  409: 'This action conflicts with existing data.',
  422: 'Some fields are invalid. Please check your input.',
  429: 'Too many requests. Try again shortly.',
  500: 'Server error. Try again shortly.',
};

function getStatusMessage(status, fallback) {
  if (status >= 500) return API_STATUS_MESSAGES[500];
  return API_STATUS_MESSAGES[status] ?? fallback;
}

function parseApiError(text) {
  try {
    const body = JSON.parse(text);
    if (typeof body.detail === 'string') {
      if (body.detail.startsWith('INVALID_TYPE:')) {
        const t = body.detail.split(':')[1] ?? 'unknown';
        return `"${t}" is not a supported question type.`;
      }
      if (API_ERROR_CODES[body.detail]) return API_ERROR_CODES[body.detail];
      // The API crafts human-readable sentences for many errors (e.g. "A survey
      // with this title already exists."). Bare machine codes are SCREAMING_SNAKE
      // with no spaces — anything with whitespace is safe to show as-is instead
      // of collapsing to the vague per-status fallback.
      return /\s/.test(body.detail) ? body.detail : null;
    }
    if (body.detail && typeof body.detail === 'object') {
      const code = body.detail.code;
      if (code && typeof code === 'string') {
        return API_ERROR_CODES[code] ?? null;
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function getApiErrorMessage(error, fallback) {
  if (error instanceof ApiError) {
    return error.message || getStatusMessage(error.status, fallback);
  }
  if (error instanceof Error) {
    return fallback;
  }
  return fallback;
}

export function messageFromAxiosError(error, fallback) {
  const status = error.response?.status ?? 500;
  const data = error.response?.data;
  let text = '';
  if (typeof data === 'string') text = data;
  else if (data) {
    try {
      text = JSON.stringify(data);
    } catch {
      text = String(data);
    }
  }
  const parsed = text ? parseApiError(text) : null;
  const safeMessage =
    parsed ?? getStatusMessage(status, error.message || fallback);
  return new ApiError(status, safeMessage);
}
